import { Reveal } from "./Reveal";
import {
  nailTreatments,
  nailEnhancements,
  waxing,
  addOns,
  pedicures,
} from "../data/menuData";

const CategoryHeading = ({ children, id }) => (
  <Reveal className="mb-10" id={id}>
    <div className="flex items-center gap-6">
      <h3 className="font-heading text-3xl md:text-5xl tracking-wide text-gold-light md:whitespace-nowrap">
        {children}
      </h3>
      <span className="h-px flex-1 bg-gradient-to-r from-gold-primary/40 to-transparent" />
    </div>
  </Reveal>
);

const MenuRow = ({ name, price, testid }) => (
  <div
    data-testid={testid}
    className="group flex items-baseline gap-3 py-3 border-b border-gold-primary/10"
  >
    <span className="font-body text-base md:text-lg text-foreground/90 group-hover:text-gold-light transition-colors duration-300">
      {name}
    </span>
    <span className="flex-1 border-b border-dashed border-gold-primary/25 mb-1.5" />
    <span className="font-heading text-lg md:text-xl text-gold-primary shrink-0">
      {price}
    </span>
  </div>
);

export default function ServiceMenu({ showHeader = true }) {
  return (
    <section
      id="menu"
      data-testid="service-menu"
      className="relative bg-ink py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        {showHeader && (
          <Reveal className="text-center mb-20 md:mb-28">
            <p className="font-body text-xs tracking-[0.4em] uppercase text-gold-muted mb-6">
              BQ Nails &amp; Spa
            </p>
            <h2 className="font-accent italic text-6xl md:text-8xl gold-gradient-text leading-none">
              Service Menu
            </h2>
            <p className="mt-8 mx-auto max-w-xl font-body text-sm md:text-base leading-relaxed text-foreground/50">
              Prices marked with &ldquo;+&rdquo; may vary based on length, design
              and detailing. We are happy to quote your look on arrival.
            </p>
          </Reveal>
        )}

        {/* Nail Treatments */}
        <div className="mb-24">
          <CategoryHeading>Nail Treatments</CategoryHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0">
            {nailTreatments.map((item) => (
              <MenuRow
                key={item.name}
                name={item.name}
                price={item.price}
                testid={`menu-treatment-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
              />
            ))}
          </div>
        </div>

        {/* Nail Enhancements — Full Set / Fill columns */}
        <div className="mb-24">
          <CategoryHeading>Nail Enhancements</CategoryHeading>
          <div className="grid grid-cols-12 pb-3 border-b border-gold-primary/20">
            <span className="col-span-6 md:col-span-8" />
            <span className="col-span-3 md:col-span-2 text-right font-body text-xs tracking-[0.2em] uppercase text-gold-muted">
              Full Set
            </span>
            <span className="col-span-3 md:col-span-2 text-right font-body text-xs tracking-[0.2em] uppercase text-gold-muted">
              Fill
            </span>
          </div>
          {nailEnhancements.map((item) => (
            <div
              key={item.name}
              data-testid={`menu-enhancement-${item.name.toLowerCase().replace(/[\s/&]+/g, "-")}`}
              className="group grid grid-cols-12 items-baseline py-3.5 border-b border-gold-primary/10"
            >
              <span className="col-span-6 md:col-span-8 font-body text-base md:text-lg text-foreground/90 group-hover:text-gold-light transition-colors duration-300">
                {item.name}
              </span>
              <span className="col-span-3 md:col-span-2 text-right font-heading text-lg md:text-xl text-gold-primary">
                {item.fullSet}
              </span>
              <span className="col-span-3 md:col-span-2 text-right font-heading text-lg md:text-xl text-foreground/40">
                {item.fill}
              </span>
            </div>
          ))}
        </div>

        {/* Waxing */}
        <div className="mb-24">
          <CategoryHeading>Waxing</CategoryHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0">
            {waxing.map((item) => (
              <MenuRow
                key={item.name}
                name={item.name}
                price={item.price}
                testid={`menu-waxing-${item.name.toLowerCase().replace(/[\s/&]+/g, "-")}`}
              />
            ))}
          </div>
        </div>

        {/* Additional Services / Add-ons */}
        <div className="mb-28">
          <CategoryHeading>Additional Services &amp; Add-ons</CategoryHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0">
            {addOns.map((item) => (
              <MenuRow
                key={item.name}
                name={item.name}
                price={item.price}
                testid={`menu-addon-${item.name.toLowerCase().replace(/[\s/.,&]+/g, "-")}`}
              />
            ))}
          </div>
        </div>

        {/* Pedicure Menu */}
        <Reveal className="text-center mb-16 mt-8">
          <span className="h-px w-24 bg-gold-primary/40 mx-auto block mb-10" />
          <h2 className="font-accent italic text-5xl md:text-7xl gold-gradient-text leading-none">
            Pedicure Menu
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:gap-8">
          {pedicures.map((p) => (
            <Reveal
              key={p.name}
              delay={0.05}
              className=""
            >
              <div
                data-testid={`menu-pedicure-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`h-full rounded-sm border p-8 md:p-10 transition-colors duration-400 hover:border-gold-primary/50 ${
                  p.tag
                    ? "border-gold-primary/40 bg-gold-primary/5"
                    : "border-gold-primary/15 bg-ink-surface"
                }`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    {p.tag && (
                      <p className="font-body text-[0.65rem] tracking-[0.3em] uppercase text-gold-primary mb-2">
                        {p.tag}
                      </p>
                    )}
                    <h4 className="font-heading text-2xl md:text-3xl text-foreground">
                      {p.name}
                    </h4>
                  </div>
                  <span className="font-heading text-3xl md:text-4xl gold-gradient-text shrink-0">
                    {p.price}
                  </span>
                </div>
                {p.description && (
                  <p className="font-body text-sm md:text-base leading-relaxed text-foreground/55 mb-6">
                    {p.description}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {p.includes.map((inc) => (
                    <span
                      key={inc}
                      className="text-[0.7rem] tracking-wide uppercase text-gold-light/80 border border-gold-primary/20 rounded-full px-3 py-1"
                    >
                      {inc}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14 text-center">
          <p className="inline-block font-heading text-2xl md:text-3xl tracking-wide text-foreground">
            Add Gel to any pedicure
            <span className="ml-3 gold-gradient-text">+$15</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
